/**
 * Floating editor popover for a gameplan line element — either a
 * single node's notes or a single edge's label (+ delete).
 *
 * Anchored by LinesCanvas absolute-positioning over the SVG, similar
 * to LinePicker. Auto-saves on blur / Enter / Cmd+Enter, closes on
 * Esc or outside click.
 */
import { useEffect, useRef, useState } from 'react';
import { useAppStore } from '../state/store';

export type EditorTarget =
  | {
      kind: 'node';
      gameplanId: string;
      lineId: string;
      nodeId: string;
      /** Current notes value — drives the initial draft. */
      initialNotes: string;
      /** Display label for the node, shown as the editor's heading. */
      displayName: string;
    }
  | {
      kind: 'edge';
      gameplanId: string;
      lineId: string;
      edgeId: string;
      initialLabel: string;
      /** "From → To" so the user knows which edge they're editing. */
      contextHeading: string;
    };

type Props = {
  target: EditorTarget;
  onClose: () => void;
};

export function LineElementEditor({ target, onClose }: Props) {
  const setLineNodeNotes = useAppStore((s) => s.setLineNodeNotes);
  const setLineEdgeLabel = useAppStore((s) => s.setLineEdgeLabel);
  const removeLineEdge = useAppStore((s) => s.removeLineEdge);

  const containerRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState<string>(
    target.kind === 'node' ? target.initialNotes : target.initialLabel,
  );

  // Outside-click → close. Same deferred-registration trick as the
  // picker so the click that opened the editor doesn't immediately
  // close it. Elements that opt out via data-bjj-no-autoclose (the
  // pencil badge that wants to TOGGLE the editor closed, the edge
  // hit overlay that toggles edge editors) are skipped — their own
  // onClick handler decides whether to close or re-open.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current) return;
      if (containerRef.current.contains(e.target as Node)) return;
      const tgt = e.target as Element | null;
      if (tgt && tgt.closest('[data-bjj-no-autoclose]')) return;
      commit();
      onClose();
    };
    const t = setTimeout(() => {
      document.addEventListener('mousedown', onDown);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('mousedown', onDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const commit = () => {
    if (target.kind === 'node') {
      if (draft !== target.initialNotes) {
        setLineNodeNotes(
          target.gameplanId,
          target.lineId,
          target.nodeId,
          draft,
        );
      }
    } else {
      if (draft !== target.initialLabel) {
        setLineEdgeLabel(
          target.gameplanId,
          target.lineId,
          target.edgeId,
          draft,
        );
      }
    }
  };

  const onDeleteEdge = () => {
    if (target.kind !== 'edge') return;
    removeLineEdge(target.gameplanId, target.lineId, target.edgeId);
    onClose();
  };

  const isNode = target.kind === 'node';
  const heading = isNode
    ? `notes · ${target.displayName}`
    : `edge · ${target.contextHeading}`;
  const placeholder = isNode
    ? 'Add a note for this step (drilling cue, grip detail, etc.)…'
    : 'Add a label for this edge (trigger, condition, branch reason)…';

  return (
    <div
      ref={containerRef}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        width: 260,
        display: 'flex',
        flexDirection: 'column',
        background: '#11141d',
        border: '1px solid rgba(155,231,255,0.30)',
        borderRadius: 8,
        boxShadow: '0 10px 24px rgba(0,0,0,0.45)',
        color: '#e6e8ef',
        overflow: 'hidden',
        zIndex: 50,
      }}
    >
      <div
        style={{
          padding: '8px 10px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          fontSize: 10,
          letterSpacing: 0.7,
          textTransform: 'uppercase',
          opacity: 0.65,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {heading}
      </div>

      {isNode ? (
        <textarea
          autoFocus
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
              e.preventDefault();
              commit();
              onClose();
            }
            // Plain Enter inserts a newline — notes are multi-line.
          }}
          style={inputStyle}
        />
      ) : (
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={placeholder}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
              onClose();
            }
          }}
          style={{ ...inputStyle, fontFamily: 'inherit' }}
        />
      )}

      <div
        style={{
          padding: '6px 10px',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          display: 'flex',
          gap: 6,
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {!isNode ? (
          <button
            onClick={onDeleteEdge}
            style={{
              background: 'transparent',
              border: '1px solid rgba(255,128,140,0.30)',
              color: '#ffaab2',
              borderRadius: 4,
              padding: '3px 8px',
              fontSize: 10,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            delete edge
          </button>
        ) : (
          <span />
        )}
        <button
          onClick={() => {
            commit();
            onClose();
          }}
          style={{
            background: 'rgba(155,231,255,0.10)',
            border: '1px solid rgba(155,231,255,0.30)',
            color: '#cdf6ff',
            borderRadius: 4,
            padding: '3px 10px',
            fontSize: 10,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            cursor: 'pointer',
            fontWeight: 600,
          }}
        >
          done
        </button>
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  margin: 8,
  background: 'rgba(0,0,0,0.40)',
  border: '1px solid rgba(255,255,255,0.10)',
  borderRadius: 4,
  color: '#e6e8ef',
  fontSize: 12,
  padding: '6px 8px',
  outline: 'none',
  resize: 'vertical',
  fontFamily: 'inherit',
  lineHeight: 1.4,
};
